import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { LoggingService } from '../../logging/logging.service';

export type SnackbarType = 'success' | 'error' | 'warning' | 'info';

export interface SnackbarConfig {
    label: string;
    type?: SnackbarType;
    timeout?: number;
    dismissible?: boolean;
    actionLabel?: string;
    position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
}

export interface SnackbarEvent extends SnackbarConfig {
    visible: boolean;
}

@Injectable({ providedIn: 'root' })
export class SnackbarService {
    private readonly translate = inject(TranslateService);
    private readonly logger = inject(LoggingService);
    private readonly snackbarSubject = new Subject<SnackbarEvent>();
    public readonly snackbar$ = this.snackbarSubject.asObservable();

    private readonly actionClickedSubject = new Subject<void>();
    public readonly actionClicked$ = this.actionClickedSubject.asObservable();

    public open(
        type: SnackbarType,
        labelOrKey: string,
        config?: Partial<Omit<SnackbarConfig, 'label' | 'type'>>
    ): void {
        this.snackbarSubject.next({
            label: this.translate.instant(labelOrKey),
            type,
            visible: true,
            timeout: config?.timeout ?? 4000,
            dismissible: config?.dismissible ?? true,
            actionLabel: config?.actionLabel,
            position: config?.position ?? 'top-center'
        });
    }

    public success(label: string, config?: Partial<Omit<SnackbarConfig, 'label' | 'type'>>): void {
        this.open('success', label, config);
    }

    public error(label: string, config?: Partial<Omit<SnackbarConfig, 'label' | 'type'>>): void {
        this.open('error', label, {
            timeout: 0,
            actionLabel: this.translate.instant('common.download-logs'),
            ...config
        });
    }

    public warning(label: string, config?: Partial<Omit<SnackbarConfig, 'label' | 'type'>>): void {
        this.open('warning', label, config);
    }

    public info(label: string, config?: Partial<Omit<SnackbarConfig, 'label' | 'type'>>): void {
        this.open('info', label, config);
    }

    public dismiss(): void {
        this.snackbarSubject.next({ label: '', visible: false });
    }

    /** @internal Called by the container component */
    public emitActionClicked(): void {
        this.logger.downloadLogs();
        this.actionClickedSubject.next();
    }
}
